import { el, svgIcon, ICONS } from './dom';
import {
  Jalali,
  todayJalali,
  jalaliFromISO,
  isoFromJalali,
  jalaliMonthLength,
  jalaliWeekday,
  jalaliDisplay,
  faDigits,
  JALALI_MONTHS,
  JALALI_WEEKDAYS,
} from './jalali';

export interface DateStrings {
  datePick: string;
  today: string;
  clear: string;
  prevMonth: string;
  nextMonth: string;
}

/** years shown per page in the year grid (and the year step of the ‹ › arrows there) */
const YEARS_PER_PAGE = 12;

/**
 * Jalali (Shamsi) date field. Replaces the old native `<input type="date">`, whose
 * calendar is always Gregorian. The trigger looks like a `.cb-finput`; clicking it
 * expands an inline Persian calendar below (inline, not floating, so it never gets
 * clipped by the chat scroll container / shadow root). The *stored* value stays a
 * Gregorian `yyyy-mm-dd` ISO string — identical to the native input — so nothing
 * downstream (forms/backend) changes; only the UI is Persian. (jalali.ts)
 */
export function renderDateField(
  key: string,
  label: string,
  values: Record<string, unknown>,
  sync: () => void,
  t: DateStrings,
): HTMLElement {
  const wrap = el('div', { class: 'cb-datewrap' });

  const valueText = el('span', { class: 'cb-dateval cb-dateph', text: t.datePick });
  const trigger = el(
    'button',
    {
      class: 'cb-finput cb-datetrigger',
      attrs: {
        type: 'button',
        'aria-label': label,
        'aria-haspopup': 'dialog',
        'aria-expanded': 'false',
      },
      on: { click: () => toggle() },
    },
    [valueText, svgIcon(ICONS.calendar, 17)],
  );

  const panel = el('div', { class: 'cb-cal', attrs: { role: 'dialog', 'aria-label': label } });
  wrap.appendChild(trigger);
  wrap.appendChild(panel);

  // `selected` is null until the visitor picks a day; `view` is the month on screen.
  let selected: Jalali | null = jalaliFromISO(String(values[key] ?? ''));
  let view: Jalali = selected ? { ...selected } : todayJalali();
  let open = false;

  function commit(): void {
    if (selected) {
      values[key] = isoFromJalali(selected.jy, selected.jm, selected.jd);
      valueText.textContent = jalaliDisplay(selected.jy, selected.jm, selected.jd);
      valueText.classList.remove('cb-dateph');
    } else {
      values[key] = '';
      valueText.textContent = t.datePick;
      valueText.classList.add('cb-dateph');
    }
    sync();
  }

  // composedPath() pierces the shadow boundary, so an outside click is any
  // mousedown whose path doesn't run through our wrapper.
  const onDocDown = (ev: Event): void => {
    if (!ev.composedPath().includes(wrap)) close();
  };
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') close();
  };

  function show(): void {
    if (open) return;
    open = true;
    panel.classList.add('open');
    trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('mousedown', onDocDown, true);
    document.addEventListener('keydown', onKey, true);
    if (selected) view = { ...selected };
    mode = 'days';
    render();
  }
  function close(): void {
    if (!open) return;
    open = false;
    panel.classList.remove('open');
    trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('mousedown', onDocDown, true);
    document.removeEventListener('keydown', onKey, true);
  }
  function toggle(): void {
    if (open) close();
    else show();
  }

  // which grid is on screen: pick a day, or jump straight to a month / year
  let mode: 'days' | 'months' | 'years' = 'days';

  /** the ‹ › arrows page by month (day view), by year (month view), or by a full
   *  page of years (year view) — whatever the current grid steps through. */
  function step(delta: number): void {
    if (mode === 'days') {
      let m = view.jm + delta;
      let y = view.jy;
      if (m < 1) {
        m = 12;
        y -= 1;
      } else if (m > 12) {
        m = 1;
        y += 1;
      }
      view = { jy: y, jm: m, jd: view.jd };
    } else if (mode === 'months') {
      view = { jy: view.jy + delta, jm: view.jm, jd: view.jd };
    } else {
      view = { jy: view.jy + delta * YEARS_PER_PAGE, jm: view.jm, jd: view.jd };
    }
    render();
  }

  /** shared header: [prev] <title> [next]. RTL flow puts «prev» on the right, so
   *  it gets the right-pointing chevron; «next» the left. The title is a button
   *  that drills up a level (day→month→year, and back down from year). */
  function header(titleText: string, onTitle: () => void): HTMLElement {
    const nav = (delta: number, aria: string, icon: string) =>
      el(
        'button',
        {
          class: 'cb-cal-nav',
          attrs: { type: 'button', 'aria-label': aria },
          on: { click: () => step(delta) },
        },
        [svgIcon(icon, 18)],
      );
    const title = el('button', {
      class: 'cb-cal-title',
      text: titleText,
      attrs: { type: 'button' },
      on: { click: onTitle },
    });
    return el('div', { class: 'cb-cal-head' }, [
      nav(-1, t.prevMonth, ICONS.chevronRight),
      title,
      nav(1, t.nextMonth, ICONS.chevronLeft),
    ]);
  }

  function render(): void {
    panel.textContent = '';
    if (mode === 'months') renderMonths();
    else if (mode === 'years') renderYears();
    else renderDays();
  }

  function renderDays(): void {
    // tap the «مرداد ۱۴۰۵» title to jump to the month grid
    panel.appendChild(
      header(`${JALALI_MONTHS[view.jm - 1]} ${faDigits(view.jy)}`, () => {
        mode = 'months';
        render();
      }),
    );

    const weekdays = el('div', { class: 'cb-cal-weekdays' });
    for (const d of JALALI_WEEKDAYS) weekdays.appendChild(el('span', { text: d }));
    panel.appendChild(weekdays);

    const grid = el('div', { class: 'cb-cal-grid' });
    const lead = jalaliWeekday(view.jy, view.jm, 1); // Sat=0 … Fri=6 → blank leading cells
    for (let i = 0; i < lead; i += 1) grid.appendChild(el('span', { class: 'cb-cal-blank' }));

    const days = jalaliMonthLength(view.jy, view.jm);
    const tj = todayJalali();
    for (let d = 1; d <= days; d += 1) {
      const isToday = tj.jy === view.jy && tj.jm === view.jm && tj.jd === d;
      const isSel =
        !!selected && selected.jy === view.jy && selected.jm === view.jm && selected.jd === d;
      grid.appendChild(
        el('button', {
          class: `cb-cal-day${isToday ? ' today' : ''}${isSel ? ' sel' : ''}`,
          text: faDigits(d),
          attrs: { type: 'button', ...(isSel ? { 'aria-pressed': 'true' } : {}) },
          on: {
            click: () => {
              selected = { jy: view.jy, jm: view.jm, jd: d };
              commit();
              close();
            },
          },
        }),
      );
    }
    panel.appendChild(grid);

    const todayBtn = el('button', {
      class: 'cb-cal-foot-btn',
      text: t.today,
      attrs: { type: 'button' },
      on: {
        click: () => {
          selected = todayJalali();
          commit();
          close();
        },
      },
    });
    const clearBtn = el('button', {
      class: 'cb-cal-foot-btn cb-cal-clear',
      text: t.clear,
      attrs: { type: 'button' },
      on: {
        click: () => {
          selected = null;
          commit();
          close();
        },
      },
    });
    panel.appendChild(el('div', { class: 'cb-cal-foot' }, [todayBtn, clearBtn]));
  }

  function renderMonths(): void {
    // tap the year title to jump to the year grid
    panel.appendChild(
      header(faDigits(view.jy), () => {
        mode = 'years';
        render();
      }),
    );
    const grid = el('div', { class: 'cb-cal-pick' });
    const tj = todayJalali();
    for (let m = 1; m <= 12; m += 1) {
      const isToday = tj.jy === view.jy && tj.jm === m;
      const isSel = !!selected && selected.jy === view.jy && selected.jm === m;
      grid.appendChild(
        el('button', {
          class: `cb-cal-cell${isToday ? ' today' : ''}${isSel ? ' sel' : ''}`,
          text: JALALI_MONTHS[m - 1],
          attrs: { type: 'button' },
          on: {
            click: () => {
              view = { jy: view.jy, jm: m, jd: view.jd };
              mode = 'days';
              render();
            },
          },
        }),
      );
    }
    panel.appendChild(grid);
  }

  function renderYears(): void {
    const start = view.jy - (((view.jy % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE);
    // tap the range title to collapse back to the month grid
    panel.appendChild(
      header(`${faDigits(start)} – ${faDigits(start + YEARS_PER_PAGE - 1)}`, () => {
        mode = 'months';
        render();
      }),
    );
    const grid = el('div', { class: 'cb-cal-pick' });
    const tj = todayJalali();
    for (let i = 0; i < YEARS_PER_PAGE; i += 1) {
      const y = start + i;
      const isToday = tj.jy === y;
      const isSel = !!selected && selected.jy === y;
      grid.appendChild(
        el('button', {
          class: `cb-cal-cell${isToday ? ' today' : ''}${isSel ? ' sel' : ''}`,
          text: faDigits(y),
          attrs: { type: 'button' },
          on: {
            click: () => {
              view = { jy: y, jm: view.jm, jd: view.jd };
              mode = 'months';
              render();
            },
          },
        }),
      );
    }
    panel.appendChild(grid);
  }

  // reflect a pre-filled ISO value on first paint (forms start empty, but be safe)
  if (selected) commit();

  return wrap;
}
